import { useEffect, useMemo, useState, type FormEvent } from "react";
import { COMPANY_DOC_CATEGORIES, type PortalView } from "@clevis/shared-types";
import { Button, Card, CardTitle, ClevisMark, FilePicker, Input, Label, cn } from "@clevis/shared-ui";
import { call } from "../../lib/callFunction";
import { SignaturePad } from "../../components/SignaturePad";

// The customer portal (app.clevis.pro/portal): a person at a customer signs
// in with a link the company emailed them (…/portal#k=…) or a code texted to
// their cell, then sees that customer's invoices (and pays them), estimates
// (and signs them), the company's W-9 and certificates, their jobs, and sends
// in POs. Sign-ins live in this browser only; one person may be let into
// several companies' portals.

type Saved = { token: string; company: string; customer: string };
const STORE = "clevis.portal.sessions";
const loadSaved = (): Saved[] => {
  try {
    const v = JSON.parse(localStorage.getItem(STORE) ?? "[]") as Saved[];
    return Array.isArray(v) ? v.filter((s) => typeof s.token === "string") : [];
  } catch {
    return [];
  }
};
const storeSaved = (list: Saved[]) => {
  try {
    localStorage.setItem(STORE, JSON.stringify(list.slice(0, 10)));
  } catch {
    /* private window: signed in for this visit only */
  }
};

const money = (c: number) => `$${(c / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
/** "Oct 14", or "Oct 14, 2025" when it isn't this year. */
const day = (ms: number) =>
  new Date(ms).toLocaleDateString(undefined, { month: "short", day: "numeric", ...(new Date(ms).getFullYear() !== new Date().getFullYear() ? { year: "numeric" } : {}) });
const when = (start: number | null) => {
  if (!start) return "Not scheduled yet";
  const d = new Date(start).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
  return `${d}, ${new Date(start).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })}`;
};
const docLabel = (id: string) => COMPANY_DOC_CATEGORIES.find((c) => c.id === id)?.label ?? id;
const startOfToday = () => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d.getTime();
};

export function PortalPage() {
  const [saved, setSaved] = useState<Saved[]>(loadSaved);
  const [active, setActive] = useState<string | null>(() => new URLSearchParams(window.location.hash.slice(1)).get("k") ?? loadSaved()[0]?.token ?? null);
  const [view, setView] = useState<PortalView | null>(null);
  const [loading, setLoading] = useState(false);
  const [notice, setNotice] = useState("");

  // A link from the email: …/portal#k=<token>. Keep it, then take it out of the address bar.
  useEffect(() => {
    const k = new URLSearchParams(window.location.hash.slice(1)).get("k");
    if (!k) return;
    const list = [{ token: k, company: "", customer: "" }, ...loadSaved().filter((s) => s.token !== k)];
    storeSaved(list);
    setSaved(list);
    window.history.replaceState(window.history.state, "", window.location.pathname);
  }, []);

  useEffect(() => {
    if (!active) {
      setView(null);
      return;
    }
    setLoading(true);
    call<PortalView>("openPortal", { token: active }, { mutation: false })
      .then((v) => {
        setView(v);
        setNotice("");
        const list = loadSaved().map((s) => (s.token === active ? { ...s, company: v.company.name, customer: v.customer.name } : s));
        storeSaved(list);
        setSaved(list);
      })
      .catch((err: Error) => {
        // That sign-in is over (ran out, or the company took access away).
        const list = loadSaved().filter((s) => s.token !== active);
        storeSaved(list);
        setSaved(list);
        setNotice(err.message);
        setView(null);
        setActive(list[0]?.token ?? null);
      })
      .finally(() => setLoading(false));
  }, [active]);

  function signedIn(sessions: Saved[]) {
    const list = [...sessions, ...loadSaved().filter((s) => !sessions.some((n) => n.token === s.token))];
    storeSaved(list);
    setSaved(list);
    setActive(sessions[0]?.token ?? null);
  }
  function signOut() {
    if (!active) return;
    void call("portalSignOut", { token: active }, { mutation: false }).catch(() => undefined);
    const list = loadSaved().filter((s) => s.token !== active);
    storeSaved(list);
    setSaved(list);
    setView(null);
    setActive(list[0]?.token ?? null);
  }
  const reload = () => {
    if (!active) return;
    void call<PortalView>("openPortal", { token: active }, { mutation: false }).then(setView).catch(() => undefined);
  };

  const contactLine = view ? [view.company.phone, view.company.email].filter(Boolean) : [];
  return (
    <div className="min-h-full bg-cream">
      <div className="mx-auto max-w-2xl space-y-3 p-4 pb-16">
        <div className="flex flex-wrap items-center gap-2 pt-2">
          {view?.company.logoUrl ? (
            <img src={view.company.logoUrl} alt={view.company.name} className="max-h-10 max-w-[160px] object-contain" />
          ) : (
            <>
              <ClevisMark size={24} />
              <span className="text-sm font-bold text-ink">{view?.company.name || "Customer portal"}</span>
            </>
          )}
          {contactLine.length ? (
            <span className="ml-auto flex gap-3 text-xs text-steel">
              {view?.company.phone ? <a href={`tel:${view.company.phone}`} className="hover:text-ink">{view.company.phone}</a> : null}
              {view?.company.email ? <a href={`mailto:${view.company.email}`} className="hover:text-ink">{view.company.email}</a> : null}
            </span>
          ) : null}
        </div>

        {notice ? <p className="rounded-lg bg-brandorange/10 px-3 py-2 text-sm text-ink">{notice}</p> : null}
        {loading && !view ? <p className="py-10 text-center text-sm text-steel">Opening your portal…</p> : null}
        {!active && !loading ? <SignIn onSignedIn={signedIn} /> : null}
        {view && active ? (
          <PortalBody
            token={active}
            view={view}
            saved={saved}
            onSwitch={(t) => setActive(t)}
            onSignOut={signOut}
            onAddCompany={() => setActive(null)}
            onChanged={reload}
          />
        ) : null}
      </div>
    </div>
  );
}

function SignIn({ onSignedIn }: { onSignedIn: (s: Saved[]) => void }) {
  const [how, setHow] = useState<"email" | "text">("email");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [stage, setStage] = useState<"ask" | "sent">("ask");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  async function go(fn: () => Promise<void>) {
    setBusy(true);
    setError("");
    try {
      await fn();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }
  const askLink = (e: FormEvent) => {
    e.preventDefault();
    void go(async () => {
      await call("requestPortalLink", { email: email.trim() }, { mutation: false });
      setStage("sent");
    });
  };
  const askCode = (e: FormEvent) => {
    e.preventDefault();
    void go(async () => {
      await call("requestPortalCode", { phone: phone.trim() }, { mutation: false });
      setStage("sent");
    });
  };
  const checkCode = (e: FormEvent) => {
    e.preventDefault();
    void go(async () => {
      const r = await call<{ sessions: Saved[] }>("verifyPortalCode", { phone: phone.trim(), code: code.trim() }, { mutation: false });
      onSignedIn(r.sessions);
    });
  };

  return (
    <Card className="space-y-3">
      <div>
        <CardTitle className="mb-0.5">Sign in to your customer portal</CardTitle>
        <p className="text-xs text-steel">See and pay invoices, sign estimates, get W-9s and insurance certificates, and send in POs. No password: we send you a link or a code.</p>
      </div>
      <div className="flex gap-1 text-xs">
        {(["email", "text"] as const).map((h) => (
          <button
            key={h}
            type="button"
            className={cn("rounded-md px-3 py-1", how === h ? "bg-ink text-white" : "text-steel ring-1 ring-steel/25 hover:text-ink")}
            onClick={() => {
              setHow(h);
              setStage("ask");
              setError("");
            }}
          >
            {h === "email" ? "Email me a link" : "Text me a code"}
          </button>
        ))}
      </div>
      {how === "email" ? (
        stage === "ask" ? (
          <form onSubmit={askLink} className="flex flex-wrap items-end gap-2">
            <div className="min-w-56 flex-1">
              <Label>Your email</Label>
              <Input type="email" required autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" />
            </div>
            <Button type="submit" busy={busy}>
              Email me a link
            </Button>
          </form>
        ) : (
          <p className="text-sm text-ink">
            Check your email. If <strong>{email}</strong> is set up for a portal, a sign-in link is on its way (it can take a minute; look in spam too).{" "}
            <button className="text-xs text-steel underline hover:text-ink" onClick={() => setStage("ask")}>
              Use another email
            </button>
          </p>
        )
      ) : stage === "ask" ? (
        <form onSubmit={askCode} className="flex flex-wrap items-end gap-2">
          <div className="min-w-56 flex-1">
            <Label>Your cell number</Label>
            <Input type="tel" required autoComplete="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="(602) 555-0100" />
          </div>
          <Button type="submit" busy={busy}>
            Text me a code
          </Button>
        </form>
      ) : (
        <form onSubmit={checkCode} className="space-y-2">
          <p className="text-sm text-ink">If {phone} is set up for a portal, a 6-digit code is on its way.</p>
          <div className="flex flex-wrap items-end gap-2">
            <div className="w-40">
              <Label>Code</Label>
              <Input inputMode="numeric" autoComplete="one-time-code" maxLength={6} required value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} placeholder="123456" />
            </div>
            <Button type="submit" busy={busy} disabled={code.length !== 6}>
              Sign in
            </Button>
            <button type="button" className="text-xs text-steel underline hover:text-ink" onClick={() => setStage("ask")}>
              Send a new code
            </button>
          </div>
        </form>
      )}
      {error ? <p className="text-xs text-danger">{error}</p> : null}
      <p className="text-[11px] text-steel">Not set up? Ask the company you work with to let you into their portal.</p>
    </Card>
  );
}

export function PortalBody({
  token,
  view,
  saved,
  onSwitch,
  onSignOut,
  onAddCompany,
  onChanged,
}: {
  token: string;
  view: PortalView;
  saved: Saved[];
  onSwitch: (token: string) => void;
  onSignOut: () => void;
  onAddCompany: () => void;
  onChanged: () => void;
}) {
  const [error, setError] = useState("");
  const [busy, setBusy] = useState<string | null>(null);
  const [allInvoices, setAllInvoices] = useState(false);
  const [allJobs, setAllJobs] = useState(false);
  const [signing, setSigning] = useState<string | null>(null);

  const openInvoices = view.invoices.filter((i) => i.status === "sent" || i.status === "partially_paid");
  const paidInvoices = view.invoices.filter((i) => i.status === "paid");
  const due = openInvoices.reduce((s, i) => s + i.totalCents - i.amountPaidCents, 0);
  const toSign = view.estimates.filter((e) => e.signUrl);
  const openJobs = view.jobs.filter((j) => j.status !== "done");
  const doneJobs = view.jobs.filter((j) => j.status === "done");
  const others = useMemo(() => saved.filter((s) => s.token !== token), [saved, token]);

  async function act(key: string, fn: () => Promise<void>) {
    setBusy(key);
    setError("");
    try {
      await fn();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(null);
    }
  }
  const pdf = (kind: "invoice" | "estimate", id: string) =>
    act(`pdf:${id}`, async () => {
      const r = await call<{ base64: string; filename: string }>("portalSalesPdf", { token, kind, id }, { mutation: false });
      const a = document.createElement("a");
      a.href = `data:application/pdf;base64,${r.base64}`;
      a.download = r.filename;
      a.click();
    });
  const pay = (id: string) =>
    act(`pay:${id}`, async () => {
      const r = await call<{ url: string }>("portalPayInvoice", { token, invoiceId: id }, { mutation: false });
      window.location.href = r.url;
    });
  const download = (d: PortalView["documents"][number]) =>
    act(`doc:${d.id}`, async () => {
      const res = await fetch("/api/portal/file", {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ docId: d.id }),
      });
      if (!res.ok) throw new Error(((await res.json().catch(() => ({}))) as { error?: string }).error ?? "That download didn't work. Try again.");
      const url = URL.createObjectURL(await res.blob());
      const a = document.createElement("a");
      a.href = url;
      a.download = d.fileName;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 10_000);
    });

  const row = "flex min-h-9 items-center gap-2 border-b border-steel/10 px-4 py-1.5 text-sm last:border-0";
  const item = "space-y-0.5 border-b border-steel/10 px-4 py-1.5 text-sm last:border-0";
  return (
    <>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-steel">
        <span>
          {view.contact.name || view.contact.email || "Signed in"} for <strong className="text-ink">{view.customer.name}</strong>
        </span>
        {others.length ? (
          <select className="rounded-md border border-steel/25 bg-white px-1.5 py-0.5 text-xs text-ink" value="" onChange={(e) => e.target.value && onSwitch(e.target.value)} aria-label="Switch company">
            <option value="">Switch company…</option>
            {others.map((s) => (
              <option key={s.token} value={s.token}>
                {s.company || "Another company"}
                {s.customer ? ` (${s.customer})` : ""}
              </option>
            ))}
          </select>
        ) : null}
        <button className="underline hover:text-ink" onClick={onAddCompany}>
          Sign in to another
        </button>
        <button className="ml-auto underline hover:text-ink" onClick={onSignOut}>
          Sign out
        </button>
      </div>

      {due > 0 || toSign.length ? (
        <div className="flex flex-wrap gap-2 text-sm">
          {due > 0 ? (
            <span className="rounded-lg bg-white px-3 py-1.5 text-sm text-ink ring-1 ring-steel/15">
              <strong>{money(due)}</strong> due on {openInvoices.length} invoice{openInvoices.length === 1 ? "" : "s"}
            </span>
          ) : null}
          {toSign.length ? (
            <span className="rounded-lg bg-white px-3 py-1.5 text-sm text-ink ring-1 ring-steel/15">
              <strong>{toSign.length}</strong> estimate{toSign.length === 1 ? "" : "s"} to review
            </span>
          ) : null}
        </div>
      ) : null}
      {error ? <p className="rounded-lg bg-danger/10 px-3 py-2 text-sm text-danger">{error}</p> : null}

      {view.invoices.length ? (
        <Card className="p-0">
          <CardTitle className="px-4 pt-3">Invoices</CardTitle>
          {[...openInvoices, ...(allInvoices ? paidInvoices : paidInvoices.slice(0, 5))].map((i) => {
            const left = i.totalCents - i.amountPaidCents;
            const late = left > 0 && i.dueAt !== null && i.dueAt < startOfToday();
            return (
              <div key={i.id} className={item}>
                <div className="flex items-baseline gap-2">
                  <span className="min-w-0 flex-1 truncate text-ink">
                    #{i.number} · {i.title}
                  </span>
                  <span className="shrink-0 font-semibold text-ink">{money(left > 0 ? left : i.totalCents)}</span>
                </div>
                <div className="flex items-center gap-3 text-xs text-steel">
                  <span className="min-w-0 flex-1 truncate">
                    {i.sentAt ? day(i.sentAt) : ""}
                    {left > 0 ? (
                      <span className={late ? "font-semibold text-danger" : ""}>
                        {i.dueAt ? ` · due ${day(i.dueAt)}` : ""}
                        {late ? " (past due)" : ""}
                      </span>
                    ) : (
                      " · paid"
                    )}
                    {left > 0 && i.amountPaidCents > 0 ? ` · ${money(i.amountPaidCents)} of ${money(i.totalCents)} paid` : ""}
                    {i.signed ? " · signed" : ""}
                  </span>
                  <button className="shrink-0 underline hover:text-ink" disabled={busy !== null} onClick={() => void pdf("invoice", i.id)}>
                    {busy === `pdf:${i.id}` ? "…" : "PDF"}
                  </button>
                  {left > 0 && view.canPayByCard ? (
                    <Button className="shrink-0 px-3 py-0.5 text-xs" busy={busy === `pay:${i.id}`} disabled={busy !== null} onClick={() => void pay(i.id)} title={view.company.cardFeePct ? `A ${view.company.cardFeePct}% card fee is added at checkout` : undefined}>
                      Pay{view.company.cardFeePct ? ` (+${view.company.cardFeePct}%)` : ""}
                    </Button>
                  ) : null}
                </div>
              </div>
            );
          })}
          {paidInvoices.length > 5 && !allInvoices ? (
            <button className="block w-full px-4 py-2 text-center text-xs text-steel hover:text-ink" onClick={() => setAllInvoices(true)}>
              Show all {paidInvoices.length} paid invoices
            </button>
          ) : null}
          {openInvoices.length && !view.canPayByCard ? <p className="px-4 pb-2 text-xs text-steel">To pay, call or email {view.company.name}.</p> : null}
        </Card>
      ) : null}

      {view.estimates.length ? (
        <Card className="p-0">
          <CardTitle className="px-4 pt-3">Estimates</CardTitle>
          {view.estimates.slice(0, 30).map((e) => (
            <div key={e.id} className={item}>
              <div className="flex items-baseline gap-2">
                <span className="min-w-0 flex-1 truncate text-ink">
                  #{e.number} · {e.title}
                </span>
                <span className="shrink-0 font-semibold text-ink">{money(e.totalCents)}</span>
              </div>
              <div className="flex items-center gap-3 text-xs text-steel">
                <span className="min-w-0 flex-1 truncate">
                  {e.sentAt ? `${day(e.sentAt)} · ` : ""}
                  {e.signUrl ? (e.expiresAt ? `good until ${day(e.expiresAt)}` : "waiting for you") : e.status}
                </span>
                <button className="shrink-0 underline hover:text-ink" disabled={busy !== null} onClick={() => void pdf("estimate", e.id)}>
                  {busy === `pdf:${e.id}` ? "…" : "PDF"}
                </button>
                {e.signUrl ? (
                  <a href={e.signUrl} className="shrink-0 rounded-lg bg-brandorange px-3 py-0.5 text-xs font-semibold text-white hover:bg-brandorange-dark">
                    Review &amp; sign
                  </a>
                ) : null}
              </div>
            </div>
          ))}
        </Card>
      ) : null}

      {view.jobs.length ? (
        <Card className="p-0">
          <CardTitle className="px-4 pt-3">Jobs</CardTitle>
          {[...openJobs, ...(allJobs ? doneJobs : doneJobs.slice(0, 5))].map((j) => (
            <div key={j.id} className={item}>
              <div className="flex items-baseline gap-2">
                <span className="min-w-0 flex-1 truncate text-ink">{j.title}</span>
                <span className={cn("shrink-0 text-xs", j.status === "in_progress" ? "font-semibold text-brandorange-dark" : j.status === "done" ? "text-success" : "text-steel")}>
                  {j.status === "done" ? "Done" : j.status === "in_progress" ? "In progress" : when(j.startAt)}
                </span>
              </div>
              <div className="truncate text-xs text-steel">{[j.where, j.address, j.poNumber ? `PO ${j.poNumber}` : ""].filter(Boolean).join(" · ") || "\u00a0"}</div>
              {j.okAt ? <div className="text-xs text-success">You OK'd this visit{j.okBy ? ` (${j.okBy})` : ""}.</div> : null}
              {j.asksOk && !j.okAt ? (
                <div className="flex flex-wrap items-center gap-2 py-0.5 text-xs">
                  <span className="text-steel">Please OK this visit so the crew can plan on it.</span>
                  <Button
                    className="px-2 py-0.5 text-xs"
                    busy={busy === `ok:${j.id}`}
                    onClick={() =>
                      void act(`ok:${j.id}`, async () => {
                        await call("portalApproveJob", { token, jobId: j.id }, { mutation: false });
                        onChanged();
                      })
                    }
                  >
                    OK this visit
                  </Button>
                </div>
              ) : null}
              {j.signedOffAt ? <div className="text-xs text-success">Signed off {new Date(j.signedOffAt).toLocaleDateString()}.</div> : null}
              {j.canSignOff ? (
                signing === j.id ? (
                  <SignoffForm
                    token={token}
                    jobId={j.id}
                    defaultName={view.contact.name}
                    onDone={() => {
                      setSigning(null);
                      onChanged();
                    }}
                    onCancel={() => setSigning(null)}
                  />
                ) : (
                  <button className="text-xs text-brandorange-dark underline" onClick={() => setSigning(j.id)}>
                    Sign off on this work
                  </button>
                )
              ) : null}
            </div>
          ))}
          {doneJobs.length > 5 && !allJobs ? (
            <button className="block w-full px-4 py-2 text-center text-xs text-steel hover:text-ink" onClick={() => setAllJobs(true)}>
              Show all {doneJobs.length} finished jobs
            </button>
          ) : null}
        </Card>
      ) : null}

      <MessagesCard token={token} company={view.company.name} messages={view.messages ?? []} onSent={onChanged} />

      {view.referral ? <ReferCard company={view.company.name} referral={view.referral} /> : null}

      {view.documents.length ? (
        <Card className="p-0">
          <CardTitle className="px-4 pt-3">Our documents</CardTitle>
          {view.documents.map((d) => (
            <div key={d.id} className={row}>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-ink">{d.name}</span>
                <span className="block text-xs text-steel">
                  {docLabel(d.category)}
                  {d.expiresAt ? ` · good through ${day(d.expiresAt)}` : ""}
                </span>
              </span>
              <Button variant="ghost" className="shrink-0 px-3 py-1 text-xs" busy={busy === `doc:${d.id}`} disabled={busy !== null} onClick={() => void download(d)}>
                Download
              </Button>
            </div>
          ))}
        </Card>
      ) : null}

      {view.canUpload ? <SendPo token={token} company={view.company.name} sent={view.sent} onSent={onChanged} /> : null}

      {view.company.requestUrl ? (
        <p className="text-center text-sm">
          <a href={view.company.requestUrl} className="text-brandorange-dark underline hover:text-brandorange">
            Request new work from {view.company.name}
          </a>
        </p>
      ) : null}
    </>
  );
}

function SendPo({ token, company, sent, onSent }: { token: string; company: string; sent: PortalView["sent"]; onSent: () => void }) {
  const [file, setFile] = useState<File | null>(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);

  async function send(e: FormEvent) {
    e.preventDefault();
    if (!file) return;
    if (file.size > 6 * 1024 * 1024) return setMsg({ ok: false, text: "That PDF is over 6 MB. Email it instead." });
    setBusy(true);
    setMsg(null);
    try {
      const pdfBase64 = await new Promise<string>((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve(String(r.result).split(",")[1] ?? "");
        r.onerror = () => reject(new Error("Couldn't read that file."));
        r.readAsDataURL(file);
      });
      await call("portalUpload", { token, fileName: file.name, pdfBase64, note: note.trim() }, { mutation: false, timeoutMs: 300_000 });
      setMsg({ ok: true, text: `Sent. ${company} has it and will book it.` });
      setFile(null);
      setNote("");
      onSent();
    } catch (err) {
      setMsg({ ok: false, text: (err as Error).message });
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card className="p-0">
      <CardTitle className="px-4 pt-3">Send us a PO or work order</CardTitle>
      <form onSubmit={send} className="space-y-2 px-4 pb-3">
        <FilePicker file={file} accept="application/pdf,.pdf" hint="One PDF, or drop it here" onChange={setFile} />
        <Input value={note} maxLength={500} placeholder="Anything we should know? (optional)" onChange={(e) => setNote(e.target.value)} />
        <div className="flex items-center gap-2">
          <Button type="submit" className="px-3 py-1 text-xs" busy={busy} disabled={!file}>
            {busy ? "Sending…" : "Send"}
          </Button>
          {msg ? <span className={cn("text-xs", msg.ok ? "text-success" : "text-danger")}>{msg.text}</span> : <span className="text-xs text-steel">One PDF at a time, up to 6 MB.</span>}
        </div>
      </form>
      {sent.length ? (
        <div className="border-t border-steel/10">
          {sent.map((s) => (
            <div key={s.id} className="flex items-center gap-2 border-b border-steel/10 px-4 py-1.5 text-sm last:border-0">
              <span className="min-w-0 flex-1 truncate text-ink">{s.subject}</span>
              <span className="shrink-0 text-xs text-steel">
                {day(s.at)} · {s.result || s.status}
              </span>
            </div>
          ))}
        </div>
      ) : null}
    </Card>
  );
}

// Sign off on finished work from the portal: the same record as signing on the tech's phone.
function SignoffForm({ token, jobId, defaultName, onDone, onCancel }: { token: string; jobId: string; defaultName: string; onDone: () => void; onCancel: () => void }) {
  const [name, setName] = useState(defaultName);
  const [note, setNote] = useState("");
  const [signature, setSignature] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  return (
    <form
      className="mt-1 space-y-2 rounded-lg border border-steel/20 bg-cream/40 p-2 text-xs"
      onSubmit={async (e) => {
        e.preventDefault();
        if (!signature) return setError("Please sign in the box.");
        setBusy(true);
        setError("");
        try {
          await call("portalSignOffJob", { token, jobId, signerName: name.trim(), signaturePngBase64: signature, note: note.trim() }, { mutation: false });
          onDone();
        } catch (err) {
          setError((err as Error).message);
        } finally {
          setBusy(false);
        }
      }}
    >
      <p className="text-ink">The work is done to your satisfaction. Sign below to confirm.</p>
      <div>
        <Label>Your name</Label>
        <Input required value={name} onChange={(e) => setName(e.target.value)} />
      </div>
      <div>
        <Label>Signature</Label>
        <SignaturePad onChange={setSignature} />
      </div>
      <div>
        <Label>Comment (optional)</Label>
        <Input value={note} onChange={(e) => setNote(e.target.value)} />
      </div>
      {error ? <p className="text-danger">{error}</p> : null}
      <div className="flex gap-2">
        <Button type="submit" busy={busy} className="px-3 py-1 text-xs">
          Sign off
        </Button>
        <Button type="button" variant="ghost" className="px-2 py-1 text-xs" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </form>
  );
}

// Notes to and from the office. The office is pushed when one arrives; replies come back here (and by email).
/** Clevis Relay: their own link, with the company's reward, ready to pass along. */
function ReferCard({ company, referral }: { company: string; referral: NonNullable<PortalView["referral"]> }) {
  const [copied, setCopied] = useState(false);
  const body = encodeURIComponent(referral.shareText);
  return (
    <Card className="space-y-2">
      <CardTitle className="mb-0">Know someone who needs {company}?</CardTitle>
      <p className="text-sm text-ink/80">
        Share your link. When their first job is paid, you get {referral.reward}.{referral.friendOffer ? ` ${referral.friendOffer}` : ""}
      </p>
      <div className="flex flex-wrap items-center gap-2">
        <code className="min-w-0 truncate rounded-md bg-cream px-2 py-1 text-xs text-ink">{referral.link}</code>
        <button
          type="button"
          className="text-xs font-semibold text-brandorange-dark hover:text-brandorange"
          onClick={() => {
            void navigator.clipboard.writeText(referral.link);
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
          }}
        >
          {copied ? "copied ✓" : "copy"}
        </button>
      </div>
      <div className="flex flex-wrap gap-2">
        <a className="rounded-lg bg-ink px-3 py-1.5 text-xs font-semibold text-white hover:bg-ink-light" href={`sms:?&body=${body}`}>
          Text a friend
        </a>
        <a className="rounded-lg border border-steel/30 px-3 py-1.5 text-xs font-semibold text-ink hover:border-steel" href={`mailto:?subject=${encodeURIComponent(company)}&body=${body}`}>
          Email a friend
        </a>
      </div>
    </Card>
  );
}

function MessagesCard({ token, company, messages, onSent }: { token: string; company: string; messages: PortalView["messages"]; onSent: () => void }) {
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  async function send(e: FormEvent) {
    e.preventDefault();
    if (!body.trim()) return;
    setBusy(true);
    setError("");
    try {
      await call("portalSendMessage", { token, body: body.trim() }, { mutation: false });
      setBody("");
      onSent();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }
  return (
    <Card className="space-y-2">
      <CardTitle className="mb-0">Messages</CardTitle>
      {messages.length === 0 ? <p className="text-xs text-steel">Questions about a job, an invoice or a date? Write to {company} here.</p> : null}
      {messages.length ? (
        <div className="max-h-72 space-y-1 overflow-y-auto text-sm">
          {messages.map((m) => (
            <div key={m.id} className={cn("max-w-[85%] rounded-lg px-2.5 py-1.5", m.direction === "out" ? "bg-cream text-ink" : "ml-auto bg-ink/90 text-white")}>
              <div className="whitespace-pre-wrap">{m.body}</div>
              <div className={cn("mt-0.5 text-[11px]", m.direction === "out" ? "text-steel" : "text-white/70")}>
                {m.direction === "out" ? `${m.byName || company}` : m.byName || "You"} ·{" "}
                {new Date(m.at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
              </div>
            </div>
          ))}
        </div>
      ) : null}
      <form className="flex items-end gap-2" onSubmit={send}>
        <textarea
          className="min-h-[44px] flex-1 rounded-lg border border-steel/30 bg-white px-2 py-1 text-sm text-ink focus:border-brandorange focus:outline-none"
          placeholder={`Message ${company}…`}
          value={body}
          onChange={(e) => setBody(e.target.value)}
        />
        <Button type="submit" busy={busy} className="px-3 py-1.5 text-sm">
          Send
        </Button>
      </form>
      {error ? <p className="text-xs text-danger">{error}</p> : null}
    </Card>
  );
}
